import React from 'react';
import Table, { columnType } from '../../../components/ClearingTable/Table';
import Button from '../../../components/Buttons/Button';
import AutoCompleteCorrespondent from '../../../components/AutoComplete/AutoCompleteCorrespondent';
import AutoCompleteAccountNo from '../../../components/AutoComplete/AutoCompleteAccountNo';
import AutoCompleteBranch from '../../../components/AutoComplete/AutoCompleteBranch';
import Text from '../../../components/Typography/Text';
import { notifyInfo, notifyError } from '../../../components/Messages/Notification';
import { getSystemDate } from '../../../services/ProfileService';
import QueryParam from '../../../services/QueryParamService';
import { IconButton, Tooltip } from '@material-ui/core';
import { Visibility as ViewIcon } from '@material-ui/icons';
import { listFeeManagerReport } from '../../../services/FeeManagerReportService';
import FeeManagerReportModal from '../../../components/Modals/FeeManagerModal';
import { CustomBodyRenderCurrencyLeft } from '../../../components/Table/CustomBodyRender';
import SelectSystemCode from '../../../components/Dropdown/SelectSystemCode';
import DateTimePicker from '../../../components/Textfields/DateTimePicker';
import AutoCompleteMasterAccountNo from '../../../components/AutoComplete/AutoCompleteMasterAccountNo';
import AutoCompleteRep from '../../../components/AutoComplete/AutoCompleteRep';

export default function FeeManager() {
  const [rows, setRows] = React.useState([]);
  const [rowData, setRowData] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [searchData, setSearchData] = React.useState(
    QueryParam.get({
      dateType: 'System Date',
      fromDate: '',
      toDate: '',
      feeType: '',
      branch: '',
      accountNo: '',
      masterAccountNo: '',
      rep: '',
      broker: '',
      correspondent: '',
    })
  );
  const [openModal, setOpenModal] = React.useState(false);

  // Table Settings
  const columns = [
    {
      name: 'actions',
      label: 'Actions',
      type: columnType.buttons,
      options: {
        customBodyRenderLite: (dataIndex) => {
          return (
            <div align={'left'} className="grd-row" style={{ margin: 0, padding: 0 }}>
              <div align={'left'} className="grd-cell" style={{ display: 'flex' }}>
                <Tooltip title="View" arrow>
                  <IconButton
                    aria-label="view"
                    onClick={() => {
                      handleOpenModal(dataIndex);
                    }}
                  >
                    <ViewIcon />
                  </IconButton>
                </Tooltip>
              </div>
            </div>
          );
        },
        sort: false,
      },
    },
    {
      name: 'accountId',
      label: 'Account ID',
      options: {
        display: false,
      },
    },
    {
      name: 'accountName',
      label: 'Account Name',
    },
    {
      name: 'accountNo',
      label: 'Account No',
    },
    {
      name: 'office',
      label: 'Branch',
    },
    {
      name: 'broker',
      label: 'Broker',
    },
    {
      name: 'correspondent',
      label: 'Correspondent',
    },
    {
      name: 'fee',
      label: 'Fee',
      options: {
        customBodyRender: CustomBodyRenderCurrencyLeft,
      },
      addFooter: true,
      type: 'amount',
    },
    {
      name: 'feeType',
      label: 'Fee Type',
    },
    {
      // Already converted to string in handlesearch
      name: 'fromDate',
      label: 'From Date',
      type: columnType.date,
    },
    {
      name: 'masterAccountNo',
      label: 'Master Account No',
    },
    {
      name: 'rep',
      label: 'Rep',
    },
    {
      name: 'status',
      label: 'Status',
    },
    {
      // Already converted to string in handlesearch
      name: 'toDate',
      label: 'To Date',
      type: columnType.date,
    },
    {
      name: 'a-selectAll',
      label: 'Select All',
      options: {
        display: true,
        print: false,
        download: false,
        filter: false,
        setCellHeaderProps: () => ({
          style: {
            width: '0px',
            display: 'table-cell',
            padding: 0,
            pointerEvents: 'none',
            fontSize: 0,
          },
        }),
      },
    },
  ];

  const options = {
    responsive: 'standard',
    selectableRows: 'none',
  };
  // End Table SettingsgetSystemDate

  const init = async () => {
    const searchParameters = localStorage.getItem('search_data');

    const { year, correspondent, branch, accountNo } = searchParameters
      ? JSON.parse(searchParameters)
      : searchData;

    const systemDate = await getSystemDate();

    setSearchData({
      ...searchData,
      year: year ? year : '',
      correspondent: correspondent ? correspondent : '',
      branch: branch ? branch : '',
      accountNo: accountNo ? accountNo : '',
    });
    if (searchData.fromDate && searchData.toDate) return;

    setSearchData({
      ...searchData,
      fromDate: systemDate,
      toDate: systemDate,
    });
  };

  React.useEffect(() => {
    init();
    // eslint-disable-next-line
  }, []);

  const handleSearchDataChange = (e) => {
    const input = e.currentTarget.name ? e.currentTarget : e.target;

    setSearchData({ ...searchData, [input.name]: input.value });
  };

  const handleSearch = async () => {
    try {
      setLoading(true);

      QueryParam.set(searchData);
      const { feeManagerReportsList } = await listFeeManagerReport(searchData);

      let rowsCopy = feeManagerReportsList;

      setRows(rowsCopy);

      notifyInfo(feeManagerReportsList.length + ' search results.');
    } catch (error) {
      notifyError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (selectedRow) => {
    setRowData(rows[selectedRow]);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
  };

  return (
    <form className="page-c">
      <div className="search-c">
        <div className="grd-row nm">
          <div className="grd-cell">
            <Text mt={8} variant="h1" label="Fee Manager" />
          </div>
          <div className="grd-cell-none">
            <Button
              disabled={loading}
              loading={loading}
              type="search"
              label={loading ? 'Searching...' : 'Search'}
              onClick={handleSearch}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <SelectSystemCode
              name="dateType"
              label="Date Type"
              placeholder="Blank"
              type="Date Type Fee"
              value={searchData.dateType}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <DateTimePicker
              required
              type="date"
              name="fromDate"
              label="From Date"
              value={searchData.fromDate}
              onChange={handleSearchDataChange}
              inputProps={{ max: searchData.toDate }}
            />
          </div>
          <div className="grd-cell">
            <DateTimePicker
              required
              type="date"
              name="toDate"
              label="To Date"
              value={searchData.toDate}
              onChange={handleSearchDataChange}
              inputProps={{ min: searchData.fromDate }}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              name="feeType"
              label="Fee Type"
              placeholder="Blank"
              type="Fee Type"
              subType="-"
              value={searchData.feeType}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteCorrespondent
              name="correspondent"
              value={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
        </div>
        <div className="grd-row">
          <div className="grd-cell">
            <AutoCompleteBranch
              name="branch"
              value={searchData.branch}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteAccountNo
              name="accountNo"
              value={searchData.accountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <AutoCompleteMasterAccountNo
              name="masterAccountNo"
              label="Master Account No"
              freeSolo={true}
              value={searchData.masterAccountNo}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            ></AutoCompleteMasterAccountNo>
          </div>
          <div className="grd-cell">
            <AutoCompleteRep
              name="rep"
              value={searchData.rep}
              correspondent={searchData.correspondent}
              onChange={handleSearchDataChange}
            />
          </div>
          <div className="grd-cell">
            <SelectSystemCode
              disabled={false}
              name="broker"
              label="Broker"
              type="Broker"
              value={searchData.broker}
              onChange={handleSearchDataChange}
            ></SelectSystemCode>
          </div>
        </div>
      </div>
      <div className="mt-20">
        <Table
          title={'Fee Manager Report'}
          data={rows}
          columns={columns}
          options={options}
        />
        {openModal && (
          <FeeManagerReportModal open={openModal} close={closeModal} data={rowData} />
        )}
      </div>
    </form>
  );
}
